<template>
  <BasePage :pageTitle="__DICT__.hub?.profile || 'Profile'" show-menu show-footer>
    <!-- Tab Switcher -->
    <div class="d-flex border-bottom mb-3">
      <button
          class="btn btn-ghost-primary flex-fill rounded-0 border-0"
          :class="{ 'active-tab': activeTab === 'week' }"
          @click="activeTab = 'week'"
      >
        {{ __DICT__.myWeek }}
      </button>
      <button
          class="btn btn-ghost-primary flex-fill rounded-0 border-0"
          :class="{ 'active-tab': activeTab === 'season' }"
          @click="activeTab = 'season'"
      >
        {{ __DICT__.tray.mySeason }}
      </button>
    </div>

    <!-- My Week Tab -->
    <div v-if="activeTab === 'week'">
      <LoadingSpinner v-if="weekLoading" />
      <template v-else-if="repInfo">
        <div class="d-flex flex-column mb-2">
          <h2 class="text-center mb-1">{{ repInfo?.Name }}</h2>
          <div
              class="badge text-white align-self-center"
              :style="{ background: getRepColor(repInfo) }"
          >
            <strong>{{ repBadgeLabel(repInfo) }}</strong>
          </div>
        </div>

        <!-- Stats Card (Nuco only) -->
        <div class="card mt-3 mb-3" v-if="__DICT__.brand === 'nuco'">
          <div class="card-header">
            <h1 class="card-title">{{ __DICT__.weeklyStats }}</h1>
          </div>
          <div class="card-body">
            <div class="row g-2">
              <div class="col-6" v-for="stat in weekStats" :key="stat.label">
                <div class="card card-sm">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <span class="avatar me-3" :class="'bg-' + stat.color + ' text-white'">
                        <component :is="stat.icon" :size="20" />
                      </span>
                      <div>
                        <div class="fw-medium">{{ stat.label }}</div>
                        <div class="text-muted">{{ stat.value }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <WeeklySchedule
            v-if="allRepsData.length > 0"
            :rep-data="repData"
            :all-reps-data="allRepsData"
            :brand="repTenant"
        />
        <RepDetails class="mt-2" :rep-info="repInfo" :loading="weekLoading"/>
        <RepTravel :rep-info="repInfo"/>
        <FlightsTable :flights="weekFlights" class="mb-2 mt-2" />

        <AccommodationList
            title="Rooms"
            :items="rooms.rooms"
            type="rooms"
        />
        <AccommodationList
            title="Inspections"
            :items="inspections.inspections"
            type="inspections"
        />
        <PaxAllocation
            accordion
            v-if="liftPassengers.length > 0"
            title="Lift Passes"
            class="mt-2"
            show-count
            :inhabitants="liftPassengers"
        />
      </template>
    </div>

    <!-- My Season Tab -->
    <div v-if="activeTab === 'season'">
      <LoadingSpinner v-if="seasonLoading" />
      <div v-else-if="seasonError" class="alert alert-danger" role="alert">
        {{ seasonError }}
      </div>
      <template v-else>
        <PersonalDetails :database-data="database" class="mb-2" />
        <AvailabilityTable :availability-data="availability" class="mb-2" />
        <FlightsTable :flights="seasonFlights" class="mb-2" />
        <Card v-if="weeks.length" title="Assigned Weeks" margin="">
          <template #actions>
            <button class="btn btn-sm btn-outline-primary" @click="assignedExpanded = !assignedExpanded">
              {{ assignedExpanded ? __DICT__.hide : __DICT__.show }}
            </button>
          </template>
          <div v-if="assignedExpanded" class="d-flex flex-column gap-2">
            <div class="card" v-for="w in weeks" :key="w.weekId">
              <div class="card-body py-3">
                <div class="d-flex justify-content-between align-items-center">
                  <div class="me-2">
                    <div class="fw-bold">{{ w.weekName }}</div>
                    <div v-if="!w.data || w.data.length === 0" class="text-muted small">
                      {{ __DICT__.season?.noInfo || 'No information at this time' }}
                    </div>
                  </div>
                  <button
                      v-if="w.data && w.data.length"
                      class="btn btn-sm btn-outline-primary"
                      @click="toggle(w.weekId)"
                  >
                    {{ expanded[w.weekId] ? __DICT__.hide : __DICT__.details }}
                  </button>
                </div>
                <div v-if="expanded[w.weekId]" class="mt-3">
                  <RepProfile :rep-info="w.data[0]" :hide-extra=true />
                </div>
              </div>
            </div>
          </div>
        </Card>
        <div v-else class="alert alert-info" role="alert">
          {{ __DICT__.season?.noInfo || 'No information at this time' }}
        </div>
      </template>
    </div>
  </BasePage>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useWeekStore } from "@/stores/week.js"
import { api } from "@/services/api.js"
import BasePage from "@/components/BasePage.vue"
import RepTravel from "@/components/RepTravel.vue"
import RepDetails from "@/components/RepDetails.vue"
import WeeklySchedule from "@/components/WeeklySchedule.vue"
import AccommodationList from "@/components/AccommodationList.vue"
import { processAccommodationData, getRepColor } from "@/utils/accommodationUtils.js"
import { repBadgeLabel } from "@/constants/staff.js"
import PaxAllocation from "@/components/PaxAllocation.vue"
import FlightsTable from "@/components/FlightsTable.vue"
import { useEnsureWeekData } from "@/composables/useEnsureWeekData.js"
import { useRotaData } from "@/composables/useRotaData.js"
import RepProfile from '@/components/RepProfile.vue'
import AvailabilityTable from '@/components/AvailabilityTable.vue'
import PersonalDetails from '@/components/PersonalDetails.vue'
import { Card, LoadingSpinner } from '@/components/atomic'
import { IconUser, IconClock, IconMoon, IconHome } from '@tabler/icons-vue'

const weekStore = useWeekStore()
const { isRecovering, isReady } = useEnsureWeekData()

// Tab state
const activeTab = ref('week')

// Week tab state
const repTenant = ref(null)
const repInfo = ref(null)
const weekLoading = ref(true)
const liftPassengers = ref([])
const weekFlights = ref([])
const rooms = ref({ pax: 0, roomCount: 0, rooms: [] })
const inspections = ref({ pax: 0, inspectionCount: 0, inspections: [] })

// Season tab state
const seasonLoading = ref(true)
const seasonError = ref(null)
const weeks = ref([])
const availability = ref({})
const database = ref({})
const seasonFlights = ref([])
const expanded = ref({})
const assignedExpanded = ref(false)
const toggle = (id) => { expanded.value[id] = !expanded.value[id] }

// Stats for nuco
const weekStats = ref([])

// Rota data composable — initialized with refs, loaded after repTenant is known
const rotaData = weekStore.repObj
  ? useRotaData(weekStore.weekId, weekStore.repObj.repId, repTenant)
  : null
const allRepsData = rotaData?.allRepsData || ref([])
const repData = rotaData?.repData || ref(null)

// Load week data
const loadWeekData = async () => {
  try {
    if (!weekStore.repObj || !rotaData) {
      weekLoading.value = false
      return
    }

    const repD = await api.get(`/api/repData/${weekStore.weekId}`)
    const repId = weekStore.repObj.repId

    repInfo.value = repD.find(rep => String(rep["GBS ID"]) === repId.toString())
    repTenant.value = repInfo.value?.tenant || null

    // Load rota data (auto-selects DB or sheet based on config flag)
    await rotaData.load(repD)

    // Staff Flights are one sheet for every brand; the API scopes them to this rep and week.
    const flightsRes = await api.get(`/api/mySeason/flights/${weekStore.weekId}`)
    weekFlights.value = Array.isArray(flightsRes) ? flightsRes : []

    const weekData = weekStore.weekData
    liftPassengers.value = weekData.filter(passenger => {
      if (!passenger.allocPlaces || !Array.isArray(passenger.allocPlaces)) return false
      return passenger.allocPlaces.some(alloc => {
        const liftpass = alloc.liftpass
        if (!liftpass) return false
        return liftpass.staffMembers?.some(staff => staff.id === parseInt(repId))
      })
    })

    const processedData = processAccommodationData(weekData, parseInt(repId))
    rooms.value = processedData.rooms
    inspections.value = processedData.inspections

    // Build stats
    weekStats.value = [
      { label: 'Host Shifts', value: repData.value?.['Count - Host'] || '0', color: 'primary', icon: IconUser },
      { label: 'Lift Passes', value: liftPassengers.value.length, color: 'info', icon: IconClock },
      { label: 'Night Patrol', value: repData.value?.['Count - Night Patrol'] || '0', color: 'warning', icon: IconMoon },
      { label: 'Rooms', value: rooms.value.roomCount, color: 'success', icon: IconHome },
    ]
  } catch (error) {
    console.error('Failed to fetch rep data:', error)
  } finally {
    weekLoading.value = false
  }
}

// Load season data
const loadSeasonData = async () => {
  try {
    const res = await api.get('/api/mySeason')
    const av = await api.get('/api/mySeason/availability')
    const db = await api.get('/api/mySeason/database')
    const flightsRes = await api.get('/api/mySeason/flights')
    weeks.value = Array.isArray(res) ? res : []
    // One shaped Availability Matrix row per rep ({ weeks: [...] }), or none.
    availability.value = (Array.isArray(av) && av[0]) || {}
    database.value = db[0]
    seasonFlights.value = Array.isArray(flightsRes) ? flightsRes : []
  } catch (e) {
    seasonError.value = e?.message || 'Failed to load My Season data.'
  } finally {
    seasonLoading.value = false
  }
}

onMounted(() => {
  loadWeekData()
  loadSeasonData()
})
</script>

<style scoped>
.active-tab {
  border-bottom: 2px solid var(--tblr-primary);
  font-weight: 600;
}
</style>
